import React from "react";

import { cn } from "../../../common/u1/cn";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import PhoneNumberInput from "../../../common/PhoneNumberInput";
import { PhoneCountrySelect } from "../../../common/u1/PhoneCountrySelect";
import { RepresentativeFields } from "./RepresentativeFields";

/* The business the order is for. */
const SectionCard = ({ title, children, className = "" }) => (
  <section
    className={cn(
      "rounded-[14px] border border-[var(--line)] bg-white p-4 sm:p-6",
      className
    )}
  >
    <h2 className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">{title}</h2>
    <div className="mt-3.5">{children}</div>
  </section>
);

export const OrderDetailsForm = ({ order, className = "" }) => {
  const {
    values,
    setField,
    setCountry,
    states,
    errors,
    rep,
    setRepField,
    acknowledged,
    setAcknowledged,
  } = order;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <SectionCard title="Business">
        <Input
          label="Full Business Name"
          labelVariant="field"
          id="owp-company-name"
          placeholder="Enter Business Name"
          value={values.companyName}
          onChange={(e) => setField("companyName", e.target.value)}
          error={errors.companyName}
        />

        <div className="mt-3.5">
          <FieldLabel htmlFor="owp-description" variant="field">
            Description about business to easily identify (Optional)
          </FieldLabel>
          <textarea
            id="owp-description"
            rows={4}
            placeholder="Write here…"
            value={values.description}
            onChange={(e) => setField("description", e.target.value)}
            className={cn(
              "w-full rounded-[var(--radius-field)] border border-[var(--field-border)]",
              "bg-white px-3.5 py-[11px] text-[14px] text-[var(--ink)] font-sans",
              "transition-[border-color,box-shadow] duration-150",
              "hover:border-[var(--field-border-hover)] focus:outline-none",
              "focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]"
            )}
          />
        </div>
      </SectionCard>

      <SectionCard title="Address">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Business Address Line 1"
            labelVariant="field"
            id="owp-address-1"
            placeholder="Suite/Building/Floor No."
            value={values.addressLine1}
            onChange={(e) => setField("addressLine1", e.target.value)}
            error={errors.addressLine1}
          />
          <Input
            label="Business Address Line 2"
            labelVariant="field"
            id="owp-address-2"
            placeholder="Enter Street/Locality"
            value={values.addressLine2}
            onChange={(e) => setField("addressLine2", e.target.value)}
          />
          <Input
            label="Area"
            labelVariant="field"
            id="owp-area"
            placeholder="Enter Area"
            value={values.area}
            onChange={(e) => setField("area", e.target.value)}
          />
          <Input
            label="Postal Code"
            labelVariant="field"
            id="owp-postal-code"
            placeholder="Enter Postal Code"
            value={values.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
          />

          {/* The shared searchable picker, not a 242-option native list. */}
          <CountrySelect
            label="Country"
            id="owp-country"
            labelVariant="field"
            value={values.country}
            onChange={(country) => setCountry(country?.code)}
            error={errors.country}
          />

          {/* Transcribed: a country with a state list gets a select, one
              without gets a text field, and neither is usable before a country
              is chosen. */}
          {states.length > 0 ? (
            <SelectField
              label="State"
              labelVariant="field"
              id="owp-state"
              value={values.state}
              placeholder="Select a state"
              options={states.map((s) => ({ value: s.name, label: s.name }))}
              onChange={(e) => setField("state", e.target.value)}
              error={errors.state}
              disabled={!values.country}
            />
          ) : (
            <Input
              label="State"
              labelVariant="field"
              id="owp-state"
              placeholder={values.country ? "Enter a state" : "Select a country first"}
              value={values.state}
              onChange={(e) => setField("state", e.target.value)}
              error={errors.state}
              disabled={!values.country}
            />
          )}

          <Input
            label="City"
            labelVariant="field"
            id="owp-city"
            placeholder="Enter a City"
            value={values.city}
            onChange={(e) => setField("city", e.target.value)}
            error={errors.city}
          />
        </div>
      </SectionCard>

      <SectionCard title="How we reach the business">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Email Address"
            labelVariant="field"
            id="owp-email"
            type="email"
            placeholder="Email Address"
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            error={errors.email}
          />

          <div>
            <FieldLabel htmlFor="owp-phone" variant="field">
              Phone
            </FieldLabel>
            <PhoneNumberInput
              id="owp-phone"
              phone={values.phone}
              setPhone={(value) => setField("phone", value || "")}
              verifyPhoneNumber={false}
              border
              /* The shared country picker behind the dial code, so it matches
                 every other phone field on the uplifted screens. */
              countrySelect={PhoneCountrySelect}
              defaultCountry="AE"
              error={errors.phone}
            />
            {errors.phone && (
              <p
                role="alert"
                className="m-0 mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans"
              >
                {errors.phone}
              </p>
            )}
          </div>
        </div>

        <RepresentativeFields
          className="mt-4"
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
          rep={rep}
          setRepField={setRepField}
        />
      </SectionCard>
    </div>
  );
};

export default OrderDetailsForm;
